import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { supabase } from './supabase.js';
import { CL } from './colors.js';
import { L } from './lang.js';
import { localiseError } from './errors.js';
import { AdminDashboard } from './App.jsx';

export function Login() {
  const nav = useNavigate();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr]           = useState('');
  const [pending, setPending]   = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (pending) return;
    setPending(true);
    setErr('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setPending(false);
    if (error) return setErr(localiseError(error));
    nav('/', { replace: true });
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', background: CL.cream }}>
      <form onSubmit={submit} style={{ width: 320 }}>
        <h1 style={{ color: CL.bark, fontFamily: 'Figtree, sans-serif',
                     fontWeight: 700, marginBottom: 20 }}>{L.loginTitle}</h1>
        <input
          type="email" required placeholder={L.loginEmail} value={email}
          onChange={e => setEmail(e.target.value)}
          style={{ display: 'block', width: '100%', padding: '8px 10px', marginBottom: 8,
                   border: `1px solid ${CL.goldL}`, borderRadius: 5, fontFamily: 'inherit',
                   fontSize: 14, background: CL.cream, color: CL.bark }}
        />
        <input
          type="password" required placeholder={L.loginPassword} value={password}
          onChange={e => setPassword(e.target.value)}
          style={{ display: 'block', width: '100%', padding: '8px 10px', marginBottom: 12,
                   border: `1px solid ${CL.goldL}`, borderRadius: 5, fontFamily: 'inherit',
                   fontSize: 14, background: CL.cream, color: CL.bark }}
        />
        {err && <p style={{ color: CL.terra, fontSize: 13, marginBottom: 10 }}>{err}</p>}
        <button type="submit" disabled={pending}
          style={{ background: CL.bark, color: CL.cream, border: 'none', width: '100%',
                   padding: '9px 16px', borderRadius: 6, cursor: pending ? 'not-allowed' : 'pointer',
                   fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
                   opacity: pending ? 0.6 : 1 }}>
          {pending ? L.loginPending : L.loginSubmit}
        </button>
      </form>
    </div>
  );
}

export function AuthGate({ children }) {
  const [state, setState] = useState({ loading: true, ok: false });
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { nav('/login', { replace: true }); return; }
      const { data: row } = await supabase
        .from('admins')
        .select('user_id')
        .eq('user_id', session.user.id)
        .limit(1)
        .maybeSingle();
      if (!row) { nav('/unauthorized', { replace: true }); return; }
      Sentry.setUser({ id: session.user.id, email: session.user.email });
      setState({ loading: false, ok: true });
    })();
  }, [nav]);

  if (state.loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', background: CL.cream,
                    color: CL.stone, fontFamily: 'Figtree, sans-serif' }}>
        Loading…
      </div>
    );
  }
  return state.ok ? children : null;
}

export function Unauthorized() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', background: CL.cream, padding: 24 }}>
      <p style={{ color: CL.bark, maxWidth: 480, textAlign: 'center',
                  fontFamily: 'Figtree, sans-serif', fontSize: 14 }}>
        {L.unauthorized}
      </p>
    </div>
  );
}

export function MobileSplashOrDashboard() {
  const [w, setW] = useState(typeof window === 'undefined' ? 1440 : window.innerWidth);

  useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (w < 1024) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', background: CL.cream, padding: 24 }}>
        <p style={{ color: CL.bark, maxWidth: 480, textAlign: 'center',
                    fontFamily: 'Figtree, sans-serif', fontSize: 14 }}>
          {L.mobileSplash}
        </p>
      </div>
    );
  }

  return <AdminDashboard />;
}
